import type { CreateLessonData } from '../services/learningTypes';
import ImagePicker from '../../../pickers/images/ImagePicker';
import { getIdValidationError, sanitizeId } from '../../../utils/idValidation';

interface LessonDetailsFormProps {
    formData: CreateLessonData;
    onChange: (field: keyof CreateLessonData, value: string | number | boolean) => void;
}

export default function LessonDetailsForm({ formData, onChange }: LessonDetailsFormProps) {
    return (
        <div className="space-y-4">
            {/* Lesson ID */}
            <div>
                <label htmlFor="id" className="block text-sm font-medium text-gray-700 mb-1">
                    Lesson ID
                </label>
                <input
                    type="text"
                    id="id"
                    value={formData.id || ''}
                    onChange={(e) => onChange('id', sanitizeId(e.target.value))}
                    placeholder="e.g., what-is-ocd"
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${getIdValidationError(formData.id || '') ? 'border-red-300' : 'border-gray-300'
                        }`}
                />
                {getIdValidationError(formData.id || '') && (
                    <p className="text-xs text-red-600 mt-1">
                        {getIdValidationError(formData.id || '')}
                    </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                    Custom ID for the lesson. Leave empty for auto-generated ID. No spaces allowed - use hyphens instead.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Title */}
                <div>
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                        Title *
                    </label>
                    <input
                        type="text"
                        id="title"
                        value={formData.title}
                        onChange={(e) => onChange('title', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="e.g., What is OCD?"
                        required
                    />
                </div>

                {/* Time Minutes */}
                <div>
                    <label htmlFor="timeMinutes" className="block text-sm font-medium text-gray-700 mb-1">
                        Duration (minutes)
                    </label>
                    <input
                        type="number"
                        id="timeMinutes"
                        value={formData.timeMinutes}
                        onChange={(e) => onChange('timeMinutes', parseInt(e.target.value) || 5)}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            </div>

            {/* Description */}
            <div>
                <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Description *
                </label>
                <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => onChange('description', e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Brief description of the lesson content..."
                    required
                />
            </div>

            {/* Image Source */}
            <ImagePicker
                value={formData.imageSrc}
                onChange={(url) => onChange('imageSrc', url)}
                folder="lessons"
                label="Lesson Image"
                placeholder="No lesson image selected"
                backgroundColor={formData.color || '#A8E6CF'}
            />
            <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                {/* Color */}
                <div>
                    <label htmlFor="color" className="block text-sm font-medium text-gray-700 mb-1">
                        Color
                    </label>

                    {/* Custom Color Picker */}
                    <div className="flex flex-wrap items-center gap-3">

                        {/* Color Swatches */}
                        <div className="flex flex-wrap gap-2">
                            {['#FFBFBF', '#FAE8E8', '#A6DDFF', '#E1EFF7', '#FFEBA2', '#F8F0D1', '#A2EED3', '#D9F4EA'].map((color) => (
                                <button
                                    key={color}
                                    type="button"
                                    onClick={() => onChange('color', color)}
                                    className={`w-10 h-10 rounded-md border-2 transition-all hover:scale-110 ${formData.color?.toUpperCase() === color.toUpperCase()
                                        ? 'border-gray-900 ring-2 ring-blue-500'
                                        : 'border-gray-300 hover:border-gray-400'
                                        }`}
                                    style={{ backgroundColor: color }}
                                    title={color}
                                />
                            ))}
                        </div>
                        <input
                            type="color"
                            id="color"
                            value={formData.color || '#A8E6CF'}
                            onChange={(e) => onChange('color', e.target.value)}
                            className="w-16 h-10 px-1 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-pointer"
                        />
                        <span className="text-sm text-gray-600">
                            Custom color: {formData.color?.toUpperCase() || '#A8E6CF'}
                        </span>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Type */}
                <div>
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                    </label>
                    <select
                        id="type"
                        value={formData.type}
                        onChange={(e) => onChange('type', e.target.value as 'lesson' | 'quiz')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="lesson">Lesson</option>
                        <option value="quiz">Quiz</option>
                    </select>
                </div>

                {/* Status */}
                <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                    </label>
                    <select
                        id="status"
                        value={formData.status}
                        onChange={(e) => onChange('status', e.target.value as 'draft' | 'published')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                    </select>
                </div>

                {/* Order */}
                <div>
                    <label htmlFor="order" className="block text-sm font-medium text-gray-700 mb-1">
                        Order
                    </label>
                    <input
                        type="number"
                        id="order"
                        value={formData.order}
                        onChange={(e) => onChange('order', parseInt(e.target.value) || 1)}
                        min="1"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                </div>
            </div>

            {/* Is Free */}
            <div className="flex items-center">
                <input
                    type="checkbox"
                    id="isFree"
                    checked={formData.isFree}
                    onChange={(e) => onChange('isFree', e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isFree" className="ml-2 block text-sm text-gray-900">
                    Free lesson (accessible without premium subscription)
                </label>
            </div>
        </div>
    );
} 